import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];
const NAVY = "#0A234A";

const PROBLEMS = [
  {
    t: "Nowhere safe to stop",
    d: "A chronic shortage of legal, secure heavy-vehicle parking forces drivers onto road shoulders and rest bays that were never built for them.",
  },
  {
    t: "Fatigue is the deadliest factor",
    d: "Driver fatigue is one of the leading contributors to serious heavy-vehicle incidents, and the hardest to manage without proper rest.",
  },
  {
    t: "Compliant hours burned circling",
    d: "Drivers waste legal driving time hunting for a spot, while dispatchers lose visibility and freight runs late.",
  },
];

export const ProblemSection = () => {
  return (
    <section
      data-testid="problem-section"
      className="relative overflow-hidden py-20 lg:py-28"
      style={{
        backgroundImage: "url(/wave-bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="relative z-10 mx-auto grid w-full max-w-[1180px] items-center gap-12 px-6 lg:grid-cols-[1.05fr_0.95fr]">
        {/* copy + cards */}
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, ease: EASE }}
            className="mb-3 text-sm font-700 uppercase tracking-[0.18em] text-[#FF5722]"
          >
            The problem
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="text-3xl font-700 leading-[1.1] tracking-tight sm:text-5xl"
            style={{ color: NAVY }}
          >
            The road is running
            <span className="block text-[#FF5722]">on empty.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.05, ease: EASE }}
            className="mt-5 max-w-xl text-base font-400 leading-relaxed text-neutral-600"
          >
            Freight keeps the economy moving, but the infrastructure that keeps
            drivers safe, rested and compliant hasn&apos;t kept up.
          </motion.p>

          <div className="mt-10 space-y-4">
            {PROBLEMS.map((p, i) => (
              <motion.div
                key={p.t}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1, ease: EASE }}
                className="rounded-2xl border border-black/5 border-l-[3px] border-l-[#FF5722] bg-white/80 p-5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_20px_40px_-24px_rgba(10,35,74,0.35)]"
              >
                <h3 className="text-[16px] font-700" style={{ color: NAVY }}>{p.t}</h3>
                <p className="mt-1 text-sm font-400 leading-relaxed text-neutral-500">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* driver image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="relative overflow-hidden rounded-[28px] border border-black/5 shadow-[0_40px_80px_-40px_rgba(10,35,74,0.5)]"
        >
          <img
            src="/assets/driver-dusk.jpg"
            alt="Truck driver beside their rig"
            className="h-full max-h-[560px] w-full object-cover"
            draggable="false"
          />
          <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/70 bg-white/85 p-4 backdrop-blur-md">
            <p className="text-[14px] font-600 leading-snug" style={{ color: NAVY }}>
              Every night, thousands of drivers face the same choice:
              <span className="text-[#FF5722]"> push on tired, or park somewhere unsafe.</span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
